// Auth gate: renders the sign-in / routing screens for every non-ready auth
// status, and the app shell once ready. Distinct screens per status —
// collapsing them causes sign-in loops.
import { useState, type ReactNode } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { Spinner } from '../components/ui'
import { useEmulators } from '../firebase-config'

function GateCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-page p-4">
      <div className="card w-full max-w-md p-8 text-center">
        <p className="text-[11px] uppercase tracking-[0.25em] text-clay">Interlochen</p>
        <h1 className="mb-1 text-2xl font-bold text-interlochen">Callboard</h1>
        <div className="mx-auto mb-5 h-0.5 w-12 rounded-full bg-sunset" aria-hidden />
        <h2 className="mb-2 text-lg font-semibold">{title}</h2>
        {children}
      </div>
    </div>
  )
}

function EmulatorSignIn() {
  const { signInDev } = useAuth()
  const [email, setEmail] = useState('director@interlochen.org')
  if (!useEmulators) return null
  return (
    <form
      className="mt-6 border-t border-hairline pt-4 text-left"
      onSubmit={(e) => {
        e.preventDefault()
        void signInDev(email)
      }}
    >
      <p className="section-label mb-2">Emulator sign-in (dev only)</p>
      <div className="flex gap-2">
        <input
          className="input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Emulator account email"
        />
        <button type="submit" className="btn-secondary shrink-0">
          Go
        </button>
      </div>
    </form>
  )
}

export function Gate({ children }: { children: ReactNode }) {
  const { status, signIn, signOutNow, error, refresh } = useAuth()

  switch (status) {
    case 'loading':
      return (
        <GateCard title="">
          <Spinner label="Signing you in…" />
        </GateCard>
      )
    case 'signed_out':
      return (
        <GateCard title="Shift schedules for Interlochen staff">
          <p className="mb-5 text-stone">Sign in with your Interlochen Google account.</p>
          <button className="btn-primary w-full" onClick={() => void signIn()}>
            Sign in with Google
          </button>
          {error && <p className="mt-3 text-sm text-danger">{error}</p>}
          <EmulatorSignIn />
        </GateCard>
      )
    case 'wrong_domain':
      return (
        <GateCard title="Interlochen accounts only">
          <p className="mb-5 text-stone">
            Callboard is available to @interlochen.org accounts. Pick your Interlochen account to continue.
          </p>
          <button className="btn-primary w-full" onClick={() => void signIn()}>
            Try a different account
          </button>
        </GateCard>
      )
    case 'not_in_group':
      return (
        <GateCard title="You're not in the Callboard access group yet">
          <p className="mb-5 text-stone">
            Ask your director to have you added to the Callboard access group. Once you're in, sign in again.
          </p>
          <button className="btn-secondary w-full" onClick={() => void refresh()}>
            Check again
          </button>
          <button className="btn-ghost mt-2 w-full" onClick={() => void signOutNow()}>
            Sign out
          </button>
        </GateCard>
      )
    case 'needs_setup':
      return (
        <GateCard title="Almost there">
          <p className="mb-5 text-stone">
            You're in! Your director still needs to assign your team and positions — they've been notified.
            Check back soon.
          </p>
          <button className="btn-secondary w-full" onClick={() => void refresh()}>
            Check again
          </button>
          <button className="btn-ghost mt-2 w-full" onClick={() => void signOutNow()}>
            Sign out
          </button>
        </GateCard>
      )
    case 'inactive':
      return (
        <GateCard title="This account is inactive">
          <p className="mb-5 text-stone">
            Your Callboard access has been deactivated. If that seems wrong, contact your director.
          </p>
          <button className="btn-ghost w-full" onClick={() => void signOutNow()}>
            Sign out
          </button>
        </GateCard>
      )
    case 'ready':
      return <>{children}</>
  }
}
